import { useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge, Card } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Select, Textarea, SearchInput, Input } from "../components/ui/Fields";
import { ConfirmDialog, Modal } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";
import type { MedalSearchResult, RankingEntry, RankingPeriod, RankingsResponse, RankingTrack, VoiceRoom } from "../api/types";

/**
 * Round 38 client item 4 — Admin Ranking Management.
 *
 * REAL FIX, replacing the old read-only version of this page (which
 * rendered a stale total_gifts_received view no longer backed by real
 * data). Now reads the real, current ranking functions for all three
 * tracks/any period, with a real search, and two real server-side
 * mutations (Deduct / Remove) — routes/admin/rankings.js. Neither
 * mutation is a client-side hide: both insert a real, permanent
 * ranking_adjustments row (migration 0089) that the ranking functions
 * themselves net into on every future read, exactly like a real gift
 * transaction would.
 */
type PendingAction = { mode: "deduct" | "remove"; row: RankingEntry };

function targetLabel(row: RankingEntry): string {
  return row.targetType === "room" ? `${row.title} (Room ID: ${row.public_room_id})` : `${row.username} (ID: ${row.public_user_id})`;
}

export function RankingsPage() {
  const [track, setTrack] = useState<RankingTrack>("wealth");
  const [period, setPeriod] = useState<RankingPeriod>("total");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<RankingEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const [pending, setPending] = useState<PendingAction | null>(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Round 40 point 2 — Ranking Test Tools. Reuses the existing user search
  // (SendMedalPage's /admin/send-medal/search) and room search
  // (RoomsPage's /admin/rooms?search=) endpoints rather than building new
  // picker components from scratch.
  const [testTrack, setTestTrack] = useState<RankingTrack>("wealth");
  const [testQuery, setTestQuery] = useState("");
  const [testUserResults, setTestUserResults] = useState<MedalSearchResult[]>([]);
  const [testRoomResults, setTestRoomResults] = useState<VoiceRoom[]>([]);
  const [isTestSearching, setIsTestSearching] = useState(false);
  const [testTarget, setTestTarget] = useState<{ targetId: string; label: string } | null>(null);
  const [testAmount, setTestAmount] = useState("");
  const [testReason, setTestReason] = useState("");
  const [isAddingTest, setIsAddingTest] = useState(false);
  const [clearScope, setClearScope] = useState<"track" | "all">("track");
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isClearingTest, setIsClearingTest] = useState(false);

  function load() {
    setIsLoading(true);
    setError(null);
    apiRequest<RankingsResponse>("/admin/rankings", { params: { track, period, search: search || undefined } })
      .then((data) => setRows(data.rows))
      .catch((err) => setError(err instanceof ApiError ? err.message : "could not load rankings"))
      .finally(() => setIsLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [track, period, search]);

  function openAction(mode: "deduct" | "remove", row: RankingEntry) {
    setPending({ mode, row });
    setAmount("");
    setReason("");
  }

  function changeTestTrack(next: RankingTrack) {
    setTestTrack(next);
    setTestQuery("");
    setTestUserResults([]);
    setTestRoomResults([]);
    setTestTarget(null);
  }

  async function testSearch() {
    if (!testQuery.trim()) return;
    setIsTestSearching(true);
    setTestUserResults([]);
    setTestRoomResults([]);
    try {
      if (testTrack === "room") {
        const rooms = await apiRequest<VoiceRoom[]>("/admin/rooms", { params: { search: testQuery.trim() } });
        setTestRoomResults(rooms.slice(0, 10));
      } else {
        const users = await apiRequest<MedalSearchResult[]>("/admin/send-medal/search", { params: { query: testQuery.trim() } });
        setTestUserResults(users);
      }
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Search failed", "error");
    } finally {
      setIsTestSearching(false);
    }
  }

  function selectTestUser(user: MedalSearchResult) {
    setTestTarget({ targetId: user.id, label: `${user.username} (ID: ${user.user_id})` });
    setTestUserResults([]);
    setTestQuery("");
  }

  function selectTestRoom(room: VoiceRoom) {
    setTestTarget({ targetId: String(room.id), label: `${room.title} (Room ID: ${room.room_id})` });
    setTestRoomResults([]);
    setTestQuery("");
  }

  async function addTestEntry() {
    if (!testTarget || !testAmount || Number(testAmount) <= 0) return;
    setIsAddingTest(true);
    try {
      await apiRequest("/admin/rankings/test-add", {
        method: "POST",
        body: {
          track: testTrack,
          targetType: testTrack === "room" ? "room" : "user",
          targetId: testTarget.targetId,
          amount: Number(testAmount),
          reason: testReason.trim() || undefined,
        },
      });
      showToast(`Added a test ${testTrack} entry for ${testTarget.label}`, "success");
      setTestTarget(null);
      setTestAmount("");
      setTestReason("");
      if (testTrack === track) load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not add test entry", "error");
    } finally {
      setIsAddingTest(false);
    }
  }

  async function clearTestData() {
    setIsClearingTest(true);
    try {
      const result = await apiRequest<{ deletedCount: number }>("/admin/rankings/test-clear", {
        method: "POST",
        body: { track: clearScope === "track" ? testTrack : undefined },
      });
      showToast(
        `Cleared ${result.deletedCount ?? 0} test ranking ${result.deletedCount === 1 ? "entry" : "entries"}${
          clearScope === "track" ? ` for ${testTrack}` : ""
        }`,
        "success"
      );
      setIsClearConfirmOpen(false);
      load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not clear test data", "error");
    } finally {
      setIsClearingTest(false);
    }
  }

  async function handleConfirm() {
    if (!pending) return;
    setIsSubmitting(true);
    try {
      const { row, mode } = pending;
      if (mode === "deduct") {
        await apiRequest("/admin/rankings/deduct", {
          method: "POST",
          body: { track, targetType: row.targetType, targetId: row.targetId, amount: Number(amount), reason: reason.trim() },
        });
        showToast(`Deducted ${Number(amount).toLocaleString()} from ${targetLabel(row)}`, "success");
      } else {
        await apiRequest("/admin/rankings/remove", {
          method: "POST",
          body: { track, targetType: row.targetType, targetId: row.targetId, reason: reason.trim() },
        });
        showToast(`Removed ${targetLabel(row)} from the ${track} ranking`, "success");
      }
      setPending(null);
      load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not complete the action", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns: Column<RankingEntry>[] =
    track === "room"
      ? [
          { header: "Rank", render: (row) => <span className="text-ink-faint">#{row.rank}</span>, className: "w-16" },
          {
            header: "Room",
            render: (row) =>
              row.targetType === "room" ? (
                <div className="flex items-center gap-3">
                  <Avatar src={row.host_avatar_url} className="h-8 w-8 rounded-full object-cover" />
                  <div>
                    <p className="flex items-center gap-2 font-medium">
                      {row.title}
                      {row.isTest && <Badge tone="warn">TEST</Badge>}
                    </p>
                    <p className="font-mono text-xs text-ink-faint">Room ID: {row.public_room_id}</p>
                  </div>
                </div>
              ) : null,
          },
          { header: "Host", render: (row) => (row.targetType === "room" ? row.host_username ?? "—" : "—") },
          {
            header: "Gift Value",
            render: (row) => <span className="font-semibold text-gold">{row.xp.toLocaleString()}</span>,
          },
          {
            header: "Actions",
            render: (row) => (
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => openAction("deduct", row)}>
                  Deduct
                </Button>
                <Button size="sm" variant="danger" onClick={() => openAction("remove", row)}>
                  Remove
                </Button>
              </div>
            ),
          },
        ]
      : [
          { header: "Rank", render: (row) => <span className="text-ink-faint">#{row.rank}</span>, className: "w-16" },
          {
            header: "User",
            render: (row) =>
              row.targetType === "user" ? (
                <div className="flex items-center gap-3">
                  <Avatar src={row.avatar_url} className="h-8 w-8 rounded-full object-cover" />
                  <div>
                    <p className="flex items-center gap-2 font-medium">
                      {row.username}
                      {row.isTest && <Badge tone="warn">TEST</Badge>}
                    </p>
                    <p className="font-mono text-xs text-ink-faint">ID: {row.public_user_id}</p>
                  </div>
                </div>
              ) : null,
          },
          {
            header: track === "charm" ? "Gifts Received" : "Gifts Sent",
            render: (row) => <span className="font-semibold text-gold">{row.xp.toLocaleString()}</span>,
          },
          {
            header: "Actions",
            render: (row) => (
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => openAction("deduct", row)}>
                  Deduct
                </Button>
                <Button size="sm" variant="danger" onClick={() => openAction("remove", row)}>
                  Remove
                </Button>
              </div>
            ),
          },
        ];

  return (
    <AppShell title="Rankings">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        Wealth/Charm periods and Room's gift-received total are live aggregates of the real gift ledger — there is
        no separate "score" row to edit. Deduct/Remove both insert a real, permanent ranking_adjustments record that
        every ranking read nets in from now on, audited the same way every other balance-affecting admin action is.
      </p>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Select label="Track" value={track} onChange={(e) => setTrack(e.target.value as RankingTrack)}>
          <option value="wealth">Wealth (gifts sent)</option>
          <option value="charm">Charm (gifts received)</option>
          <option value="room">Room (by room, gifts received)</option>
        </Select>
        <Select label="Period" value={period} onChange={(e) => setPeriod(e.target.value as RankingPeriod)}>
          <option value="daily">Daily</option>
          <option value="weekly">Last Week</option>
          <option value="monthly">Last Month</option>
          <option value="total">Total</option>
        </Select>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={track === "room" ? "Search by room title, host, or Room ID" : "Search by username or User ID"}
        />
      </div>

      <Card className="mb-4 max-w-2xl p-5">
        <h3 className="mb-1 text-sm font-semibold text-ink">Ranking Test Tools</h3>
        <p className="mb-4 text-xs text-ink-faint">
          Adds a real ranking_adjustments entry through the exact same server-side path as Deduct/Remove above —
          genuinely affects what the ranking functions compute, just flagged as test data (migration 0097) so it can
          be told apart and safely bulk-deleted below without touching any real admin correction.
        </p>

        <div className="mb-3 flex flex-wrap items-end gap-3">
          <Select label="Track" value={testTrack} onChange={(e) => changeTestTrack(e.target.value as RankingTrack)}>
            <option value="wealth">Wealth (gifts sent)</option>
            <option value="charm">Charm (gifts received)</option>
            <option value="room">Room</option>
          </Select>
          <SearchInput
            value={testQuery}
            onChange={setTestQuery}
            placeholder={testTrack === "room" ? "Search rooms by title..." : "Search users by username or ID..."}
          />
          <Button variant="secondary" onClick={testSearch} isLoading={isTestSearching} disabled={!testQuery.trim()}>
            Search
          </Button>
        </div>

        {(testUserResults.length > 0 || testRoomResults.length > 0) && (
          <div className="mb-3 divide-y divide-surface-border rounded-lg border border-surface-border">
            {testTrack === "room"
              ? testRoomResults.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => selectTestRoom(room)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-raised"
                  >
                    <span>{room.title}</span>
                    <span className="font-mono text-xs text-ink-faint">Room ID: {room.room_id}</span>
                  </button>
                ))
              : testUserResults.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => selectTestUser(user)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-raised"
                  >
                    <span>{user.username}</span>
                    <span className="font-mono text-xs text-ink-faint">ID: {user.user_id}</span>
                  </button>
                ))}
          </div>
        )}

        {testTarget && (
          <div className="mb-3 flex items-center justify-between rounded-lg border border-surface-border bg-surface-raised px-3 py-2">
            <span className="text-sm text-ink">{testTarget.label}</span>
            <Button size="sm" variant="ghost" onClick={() => setTestTarget(null)}>
              Change
            </Button>
          </div>
        )}

        <div className="mb-3 flex flex-wrap items-end gap-3">
          <Input
            label="Amount"
            type="number"
            min={1}
            placeholder="e.g. 500"
            value={testAmount}
            onChange={(e) => setTestAmount(e.target.value)}
          />
          <Textarea
            label={'Reason (optional — defaults to "Admin ranking test entry")'}
            value={testReason}
            onChange={(e) => setTestReason(e.target.value)}
            rows={1}
          />
          <Button onClick={addTestEntry} isLoading={isAddingTest} disabled={!testTarget || !testAmount || Number(testAmount) <= 0}>
            Add Test Entry
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t border-surface-border pt-3">
          <Select label="Clear scope" value={clearScope} onChange={(e) => setClearScope(e.target.value as "track" | "all")}>
            <option value="track">This track only ({testTrack})</option>
            <option value="all">All tracks</option>
          </Select>
          <Button variant="danger" onClick={() => setIsClearConfirmOpen(true)}>
            Clear All Test Data
          </Button>
        </div>
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.targetId}
        isLoading={isLoading}
        error={error}
        onRetry={load}
        emptyTitle="No ranked activity for this selection"
      />

      <Modal
        isOpen={pending !== null}
        onClose={() => !isSubmitting && setPending(null)}
        title={pending?.mode === "deduct" ? "Deduct ranking value" : "Remove from ranking"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPending(null)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirm}
              isLoading={isSubmitting}
              disabled={!reason.trim() || (pending?.mode === "deduct" && (!amount || Number(amount) <= 0))}
            >
              {pending?.mode === "deduct" ? "Deduct" : "Remove"}
            </Button>
          </>
        }
      >
        {pending && (
          <div className="space-y-3">
            <p className="text-sm text-ink-muted">
              {pending.mode === "remove"
                ? `Zero out ${targetLabel(pending.row)}'s current ${track} total, removing them from the ranking.`
                : `Deduct an amount from ${targetLabel(pending.row)}'s ${track} total.`}
            </p>
            {pending.mode === "deduct" && (
              <Input
                label="Amount to deduct"
                type="number"
                min={1}
                placeholder="e.g. 500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            )}
            <Textarea
              label="Reason (required — recorded in the ranking adjustment and audit log)"
              placeholder="e.g. Fraudulent gifting ring, chargeback on the underlying purchase..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={isClearConfirmOpen}
        onClose={() => !isClearingTest && setIsClearConfirmOpen(false)}
        onConfirm={clearTestData}
        isLoading={isClearingTest}
        isDangerous
        title="Clear test ranking data"
        confirmLabel="Clear"
        description={
          clearScope === "track"
            ? `Permanently delete every test ranking entry for the ${testTrack} track. Real admin corrections (Deduct/Remove) are never affected.`
            : "Permanently delete every test ranking entry across all tracks. Real admin corrections (Deduct/Remove) are never affected."
        }
      />
    </AppShell>
  );
}
